import { List, ListItemText, ListItemButton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router";
import "../../App.css";
import {
  CHANGE_CARD_MODAL,
  DELETE_CARD_MODAL,
  GET_CARDS_AS_PDF,
  GET_EXTENDED_CARD_FOR_PERSON,
  ORDER_CARD,
} from "../../redux/reducers/cardReducer";
import { CHANGE_CONSENT_MODAL } from "../../redux/reducers/personReducer";
import PrintIcon from "@mui/icons-material/Print";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import AddCardIcon from "@mui/icons-material/AddCard";
import AddIcon from "@mui/icons-material/Add";
import { ADD_TRANSACTION_MODAL } from "../../redux/reducers/transactionReducer";
import { CarePrincipalSettings, User } from "../../generated/api";
import {
  isCardDeletable,
  isCardEditable,
  isPrivileged,
} from "../../util/PrivilegeUtil";
import { useEffect, useState } from "react";

export function SearchCardSubMenu() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const carePrincipalId = useSelector(
    (state: any) => state.session.selectedCarePrincipal.id
  );
  const cardWithHistory = useSelector(
    (store: any) => store.card.cardWithHistory
  );
  const currentCard = useSelector(
    (state: any) => state.card.cardWithHistory.current
  );
  const historyCardId = useSelector(
    (state: any) => state.card.historyCard
  );
  const currentUser: User = useSelector(
    (state: any) => state.session.currentUser
  );
  const personName: User = useSelector(
    (state: any) => state.person.name
  );
  const searchForm = useSelector((state: any) => state.card.params);
  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const carePrincipalSettings: CarePrincipalSettings = useSelector(
    (store: any) => store.carePrincipal.carePrincipalSettings
  );

  const [selectedCard, setSelectedCard] = useState(currentCard);

  const isHistory = location.pathname.includes("history");
  const isRegisterCard = location.pathname.endsWith("register-card");
  const isTransaction = location.pathname.includes("manage-transactions") || isHistory;

  useEffect(() => {
    if (extendedCard && isHistory) {
      setSelectedCard(extendedCard);
    } else {
      setSelectedCard(currentCard);
    }
  }, [extendedCard, currentCard, isHistory]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleViewTransactions = () => {
    const params = {
      carePrincipalId: carePrincipalId,
      personId: searchForm.personId,
      cardTypeId: searchForm.cardTypeId,
    };
    dispatch({ type: GET_EXTENDED_CARD_FOR_PERSON, params });
  };

  const deleteCard = () => {
    const params = {
      carePrincipalId: carePrincipalId,
      cardSystemId: selectedCard.cardSystemId,
    };
    dispatch({ type: DELETE_CARD_MODAL, params, searchForm });
  };

  const getCardsAsPdf = () => {
    const params = {
      carePrincipalId: carePrincipalId,
      cardSystemIds: isHistory && historyCardId ? [historyCardId] : [currentCard.cardSystemId],
      careProviderId: currentUser.healthCareProviderHsaId,
      isPdfSummary: isTransaction ? false : true,
      patientName: personName ? personName : "",
    };
    dispatch({ type: GET_CARDS_AS_PDF, params });
  };

  const orderCard = () => {
    const params = {
      carePrincipalId: carePrincipalId,
      personId: searchForm.personId,
      cardSystemId: currentCard.cardSystemId,
      overrideOrderLimit: true,
    };
    dispatch({ type: ORDER_CARD, params, searchForm });
  };

  return (
    <List sx={{ marginTop: "1vh" }}>
      <Stack direction="row" alignItems="center">
        {isPrivileged(
          currentUser.privileges,
          "EFK.ACCOUNT.MODIFY",
          "WRITE"
        ) &&
        isPrivileged(currentUser.privileges,
          "EFK.ACCOUNT.DELETE",
          "WRITE"
        ) &&
          !isHistory &&
          !isTransaction && (
            <ListItemButton
              key="managePerson"
              component={NavLink}
              to="/search-card/manage-person"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <PersonPinIcon />
                <ListItemText>
                  {t("searchCard.managePerson.title")}
                </ListItemText>
              </Stack>
            </ListItemButton>
          )}
        {((selectedCard && selectedCard.status !== "BELOW_LIMIT") ||
          isTransaction) && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key="print"
              onClick={() => getCardsAsPdf()}
              disabled={selectedCard?.cardStatus === "BELOW_LIMIT"}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <PrintIcon />
                {!isTransaction ? (
                  <ListItemText>{t("searchCard.menu.printCard")}</ListItemText>
                ) : (
                  <ListItemText>
                    {t("searchCard.menu.printTransactions")}
                  </ListItemText>
                )}
              </Stack>
            </ListItemButton>
          )}

        {!isHistory &&
          selectedCard &&
          selectedCard.status === "FINAL" &&
          !isHistory &&
          carePrincipalSettings?.orderCardEnabled &&
          isPrivileged(currentUser.privileges, "EFK.CARD.ORDER", "WRITE") && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key="orderCard"
              onClick={() => orderCard()}
            >
              <div>
                <Stack
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  textAlign="center"
                >
                  <EmailIcon />
                  <ListItemText>{t("searchCard.menu.orderCard")}</ListItemText>
                </Stack>
              </div>
            </ListItemButton>
          )}

        {selectedCard &&
          isCardEditable(selectedCard, currentUser, carePrincipalId) && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key="changeCard"
              onClick={() => dispatch({ type: CHANGE_CARD_MODAL })}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <EditIcon />
                <ListItemText>{t("searchCard.menu.changeCard")}</ListItemText>
              </Stack>
            </ListItemButton>
          )}

        {selectedCard &&
          isCardDeletable(selectedCard, currentUser, carePrincipalId) && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key="deleteCard"
              onClick={() => deleteCard()}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <DeleteIcon />
                <ListItemText>{t("searchCard.menu.deleteCard")}</ListItemText>
              </Stack>
            </ListItemButton>
          )}

        {selectedCard &&
          !isHistory &&
          isPrivileged(currentUser.privileges, "EFK.CARD.ADD", "WRITE") &&
          !isRegisterCard &&
          !isHistory &&
          selectedCard?.status !== "PRELIMINARY" &&
          selectedCard?.status !== "FINAL" && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key="createCard"
              component={NavLink}
              to={"/search-card/create-card"}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <AddCardIcon />
                <ListItemText>{t("searchCard.menu.createCard")}</ListItemText>
              </Stack>
            </ListItemButton>
          )}

        {!selectedCard &&
          !isHistory &&
          isPrivileged(currentUser.privileges, "EFK.CARD.ADD", "WRITE") && (
            <ListItemButton
              key="registerCard"
              component={NavLink}
              to={"/search-card/register-card"}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <AddCardIcon />
                <ListItemText>{t("searchCard.menu.registerCard")}</ListItemText>
              </Stack>
            </ListItemButton>
          )}

        {selectedCard && !isHistory && !isTransaction && (
          <ListItemButton
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            key="manageTransactions"
            component={NavLink}
            to={"/search-card/manage-transactions"}
            onClick={() => handleViewTransactions()}
          >
            <Stack
              display="flex"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <ReceiptIcon />
              <ListItemText>{t("searchCard.manageTxns.title")}</ListItemText>
            </Stack>
          </ListItemButton>
        )}

        {isTransaction &&
          isPrivileged(
            currentUser.privileges,
            "EFK.ACCOUNT.MODIFY",
            "WRITE"
          ) && (
            <ListItemButton
              key="changeConsent"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => dispatch({ type: CHANGE_CONSENT_MODAL })}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <ChangeCircleIcon />
                <ListItemText>
                  {t("searchCard.modals.changeConsent")}
                </ListItemText>
              </Stack>
            </ListItemButton>
          )}

        {cardWithHistory &&
          !isHistory &&
          isPrivileged(
            currentUser.privileges,
            "EFK.TRANSACTION.ADD",
            "WRITE"
          ) && (
            <ListItemButton
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              alignItems="flex-start"
              onClick={() => dispatch({ type: ADD_TRANSACTION_MODAL })}
            >
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <AddIcon />
                <ListItemText>
                  {t("searchCard.manageTxns.addTransaction")}
                </ListItemText>
              </Stack>
            </ListItemButton>
          )}
      </Stack>
    </List>
  );
}
